<template>
    <div class="widget-parameter-setup-card rounded-lg p-3">
        <!-- Stacked in the edit sidebar, where a row leaves the text column a word wide -->
        <div
            :class="updateMode
                ? 'flex flex-col items-start gap-2.5'
                : 'flex items-center justify-between gap-4'"
        >
            <div class="min-w-0">
                <div class="flex items-center gap-1 mb-0.5">
                    <RedesignBaseIcon
                        v-show="!isConfigured"
                        name="AlertTriangle"
                        class="w-4 h-4 text-warning shrink-0"
                    />
                    <span class="font-semibold truncate">
                        {{ $t('widget.parameterSetup.title') }}
                    </span>
                </div>

                <!-- Every parameter is required, so one status answers for all of them -->
                <ParameterConfiguredBadge :configured="isConfigured" />

                <p class="text-xs text-gray-500 mt-1.5">
                    {{ $t('widget.parameterSetup.description') }}
                </p>
            </div>

            <RedesignBaseButton
                size="small"
                type="outline"
                icon="Edit"
                class="shrink-0"
                @click="modalVisible = true"
            >
                {{ $t('widget.aiInstructions.configure') }}
            </RedesignBaseButton>
        </div>

        <RedesignFormError
            v-if="shownError"
            :error-message="shownError"
            class="mt-2"
        />

        <WidgetParameterSetupModal
            :visible="modalVisible"
            :configs="configs"
            @save="onSave"
            @update:visible="val => modalVisible = val"
        />
    </div>
</template>

<script setup lang="ts">
import { computed, ref } from 'vue'

import RedesignBaseIcon from '@/components/RedesignBaseComponents/RedesignBaseIcon.vue'
import RedesignBaseButton from '@/components/RedesignBaseComponents/Buttons/RedesignBaseButton.vue'
import RedesignFormError from '@/components/RedesignBaseComponents/Form/RedesignFormError.vue'
import ParameterConfiguredBadge
    from '@/components/RedesignComponents/Widgets/Parameters/ParameterConfiguredBadge.vue'
import WidgetParameterSetupModal
    from '@/components/RedesignComponents/Widgets/Parameters/WidgetParameterSetupModal.vue'
import {
    findInvalidWizardParameterIndex,
    getWizardParameters
} from '@/components/RedesignComponents/Widgets/widgetParameterRegistry'
import { WidgetConfig } from '@/types/dashboard'
import i18n from '@/i18n'

const props = defineProps<{
    configs: Array<WidgetConfig>
    updateMode?: boolean
}>()

const emit = defineEmits<{
    (e: 'update:config', value: WidgetConfig): void
}>()

const modalVisible = ref(false)
const shownError = ref('')

const isConfigured = computed(() => {
    return findInvalidWizardParameterIndex(getWizardParameters(props.configs)) === -1
})

function onSave (configs: Array<WidgetConfig>) {
    configs.forEach(config => emit('update:config', config))
    shownError.value = ''
}

function validate (): boolean {
    return isConfigured.value
}

function showError () {
    shownError.value = String(i18n.t('validation.required'))
}

function clearError () {
    shownError.value = ''
}

defineExpose({
    validate,
    showError,
    clearError
})
</script>

<style scoped>
.widget-parameter-setup-card {
    background-color: color-mix(in srgb, var(--primary-color) 5%, transparent);
    border: 2px solid color-mix(in srgb, var(--primary-color) 20%, transparent);
}
</style>
